import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, mxn, fmtDate } from "@/lib/api";
import { Printer } from "lucide-react";

export default function ReportePrint() {
  const { type } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    const qs = params.toString();
    api.get(`/reports/${type}${qs ? `?${qs}` : ""}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [type, params]);

  useEffect(() => {
    document.body.style.background = "#dfe4ea";
    return () => (document.body.style.background = "");
  }, []);

  if (data === null) return <div className="p-10 font-mono-data text-[#5B6672]">Generando reporte…</div>;
  if (data === false) return <div className="p-10 font-mono-data text-[#ED1F26]">No se pudo generar el reporte.</div>;

  const { label, items, totals, filters, company, has_totals } = data;
  const rango = filters.date_from || filters.date_to
    ? `${filters.date_from ? fmtDate(filters.date_from) : "inicio"} — ${filters.date_to ? fmtDate(filters.date_to) : "hoy"}`
    : "Todos los registros";

  return (
    <div className="min-h-screen py-8 flex flex-col items-center">
      <div className="no-print mb-6 flex gap-3">
        <button onClick={() => window.print()} data-testid="print-report-button"
          className="inline-flex items-center gap-2 bg-[#465B7F] hover:bg-[#374A66] text-white px-5 py-2.5 text-sm uppercase tracking-widest transition-colors">
          <Printer size={16} /> Imprimir relación
        </button>
        <button onClick={() => window.close()} className="px-5 py-2.5 text-sm uppercase tracking-widest border border-[#16202B] text-[#16202B] hover:bg-[#16202B] hover:text-white transition-colors">
          Cerrar
        </button>
      </div>

      <div className="bg-white shadow-lg" style={{ width: "279mm", minHeight: "180mm", padding: "16mm" }}>
        <div className="h-1.5 brand-gradient -mx-[16mm] -mt-[16mm] mb-6" />
        <div className="flex justify-between items-start border-b-2 border-[#16202B] pb-4">
          <img src="/tas-logo.webp" alt="TAS Publicidad 360" className="w-44" />
          <div className="text-right">
            <div className="font-display font-bold text-2xl text-[#465B7F]">RELACIÓN DE {label.toUpperCase()}</div>
            <div className="font-mono-data text-xs text-[#5B6672] mt-1">Periodo: {rango}</div>
            <div className="font-mono-data text-xs text-[#5B6672]">Emitido: {fmtDate(new Date().toISOString())}</div>
          </div>
        </div>

        <table className="w-full mt-6 font-mono-data text-sm">
          <thead>
            <tr className="border-b-2 border-[#16202B] text-left">
              <th className="py-2 uppercase text-xs tracking-widest">Folio</th>
              <th className="py-2 uppercase text-xs tracking-widest">Fecha</th>
              <th className="py-2 uppercase text-xs tracking-widest">Cliente</th>
              <th className="py-2 uppercase text-xs tracking-widest">Estatus</th>
              {has_totals && <th className="py-2 uppercase text-xs tracking-widest text-right">Total</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-8 text-center text-[#5B6672]">No hay registros en este periodo.</td></tr>
            ) : items.map((d) => (
              <tr key={d.id} className="border-b border-[#E3E8EE]">
                <td className="py-2 text-[#16202B]">{d.number}</td>
                <td className="py-2">{fmtDate(d.created_at)}</td>
                <td className="py-2">{d.client?.name || "—"}</td>
                <td className="py-2">{d.status || (d.status === undefined ? "—" : d.status)}</td>
                {has_totals && <td className="py-2 text-right text-[#16202B]">{mxn(d.total)}</td>}
              </tr>
            ))}
          </tbody>
          {has_totals && items.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[#16202B] font-semibold">
                <td className="py-2" colSpan={4}>{totals.count} documento(s) · Subtotal {mxn(totals.subtotal)} · IVA {mxn(totals.iva)}</td>
                <td className="py-2 text-right text-[#465B7F]">{mxn(totals.total)}</td>
              </tr>
              {type === "facturas" && (
                <tr className="font-mono-data text-xs text-[#5B6672]">
                  <td className="py-1" colSpan={4}>Cobrado {mxn(totals.paid)}</td>
                  <td className="py-1 text-right text-[#ED1F26]">Saldo {mxn(totals.balance)}</td>
                </tr>
              )}
            </tfoot>
          )}
          {!has_totals && items.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[#16202B] font-semibold">
                <td className="py-2" colSpan={5}>{totals.count} documento(s)</td>
              </tr>
            </tfoot>
          )}
        </table>

        <div className="mt-12 font-mono-data text-xs text-center text-[#5B6672] border-t border-[#E3E8EE] pt-4">
          {company?.name || "TAS Publicidad 360"}
        </div>
      </div>
    </div>
  );
}
